import { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react'
import {
  applyViewPreferenceAttributes,
  normalizeViewPreferences,
  readViewPreferences,
  writeViewPreferences,
} from './viewPreferencesStore'

const ViewPreferencesContext = createContext(null)

export function ViewPreferencesProvider({ children }) {
  const [preferences, setPreferences] = useState(readViewPreferences)

  useLayoutEffect(() => {
    const normalized = normalizeViewPreferences(preferences)
    applyViewPreferenceAttributes(normalized)
    writeViewPreferences(normalized)
  }, [preferences])

  const value = useMemo(() => ({
    preferences,
    setViewPreference(key, nextValue) {
      setPreferences((current) => normalizeViewPreferences({ ...current, [key]: nextValue }))
    },
  }), [preferences])

  return <ViewPreferencesContext.Provider value={value}>{children}</ViewPreferencesContext.Provider>
}

const DEFAULT_CONTEXT = Object.freeze({
  preferences: normalizeViewPreferences({}),
  setViewPreference() {},
})

/**
 * The operator's display preferences.
 *
 * options.optional: outside a ViewPreferencesProvider, return the default
 * preferences (setting one does nothing) instead of throwing. For shared
 * components, such as DataTable, that a page can render under a test or
 * preview without the provider. Left out, a missing provider throws as before.
 */
export function useViewPreferences({ optional = false } = {}) {
  const context = useContext(ViewPreferencesContext)
  if (!context) {
    if (optional) return DEFAULT_CONTEXT
    throw new Error('useViewPreferences must be used inside ViewPreferencesProvider')
  }
  return context
}

/** Like useViewPreferences, but a component rendered outside the provider gets the default preferences instead of an error. */
export function useOptionalViewPreferences() {
  const context = useContext(ViewPreferencesContext)
  return context || { preferences: normalizeViewPreferences({}) }
}
